import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { CSSProperties, RefObject } from 'react'
import type {
  CalendarLeave,
  LeaveStatus,
  PublicHoliday,
  Blackout,
} from '@lms/shared-types'
import { PersonalBlock } from '../../components/employee/PersonalBlock'
import { ForfeitureBanner } from '../../components/employee/ForfeitureBanner'
import { UpcomingLeaveList } from '../../components/employee/UpcomingLeaveList'
import { useHealthBar } from '../../api/health-bar'
import { flags } from '../../lib/feature-flags'
import { listMyBalances, listMyLeaves } from '../../api/me'
import { listEmployeeBalances, listEmployeeLeaves } from '../../api/employees'
import { getCalendar } from '../../api/calendar'
import { useSession } from '../../auth/auth-context'
import { useEffectiveEmployeeId } from '../../components/shell/effective-employee'
import { Icon } from '../../components/primitives/Icon'
import { TeamCalendarGrid } from '../../components/employee/TeamCalendarGrid'
import { buildMonthCells } from '../../components/primitives/MonthGrid'
import { CalendarDayPopover } from '../../components/primitives/CalendarDayPopover'

/**
 * Statuses shown on the personal home calendar: anything the employee has
 * actively applied for (pending, its HR-first-review holding state, and its
 * composite escalation states) plus approved leave. Drafts, rejected, and
 * cancelled leaves are excluded — they aren't part of the forward plan.
 *
 * ``pending_hr_review`` is included: to the owner it's a live request they've
 * applied for (shown as "Pending"), so it belongs on their calendar + upcoming
 * list just like ``pending``.
 */
export const HOME_CALENDAR_STATUSES: ReadonlySet<LeaveStatus> = new Set<LeaveStatus>([
  'pending',
  'pending_hr_review',
  'escalated',
  'approved',
])

/** True when a leave belongs to `employeeId` and is in an active (plannable) state. */
export function isHomeCalendarLeave(
  leave: CalendarLeave,
  employeeId: string | null,
): boolean {
  return leave.employeeId === employeeId && HOME_CALENDAR_STATUSES.has(leave.status)
}

// ─── helpers ─────────────────────────────────────────────────────────────────

export function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

export function toISO(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

export function toMonthKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`
}

export function shiftMonthDate(d: Date, delta: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1)
}

function monthLabel(d: Date): string {
  return d.toLocaleString('en', { month: 'long' })
}

// BalancesCard, BalanceTile, and their gender/birthday/probation helpers
// moved to packages/web/src/components/employee/PersonalBlock.tsx so every
// role's home page can render the personal block at the top.

// ─── TwoMonthHome ─────────────────────────────────────────────────────────────
// Personal two-month planning calendar. Shares the exact grid + leave-bar UI
// and click-to-open day popover used by the full Team Calendar (/calendar), but
// scoped to the viewer's own leaves only — teammate availability lives on the
// dedicated Team Calendar.

export interface TwoMonthHomeProps {
  baseMonth: Date
  /** Viewer whose own leaves are shown (effective employee when impersonating). */
  viewerId: string | null
  todayISO: string
  remainingDays: number
  nextLeaveDate: string | null
  /** How many months to render side-by-side. Default 2; the demoted home
   *  planning calendar passes 1 to halve its vertical footprint. */
  monthsToShow?: 1 | 2
}

export function TwoMonthHome({
  baseMonth,
  viewerId,
  todayISO,
  remainingDays,
  nextLeaveDate,
  monthsToShow = 2,
}: TwoMonthHomeProps): JSX.Element {
  const [offset, setOffset] = useState(0)

  // Slide the two-month strip left/right on navigation — mirrors the month
  // motion on the /apply RangeCalendar (Web Animations API on the grid, so the
  // cells don't remount mid-animation). Honors prefers-reduced-motion.
  const monthsRef = useRef<HTMLDivElement | null>(null)
  const prevOffsetRef = useRef(offset)
  useEffect(() => {
    const prev = prevOffsetRef.current
    if (prev === offset) return
    const isForward = offset > prev
    prevOffsetRef.current = offset
    const el = monthsRef.current
    if (!el || typeof el.animate !== 'function') return
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return
    }
    const dx = isForward ? 28 : -28
    el.animate(
      [
        { transform: `translateX(${dx}px)`, opacity: 0.55 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      { duration: 240, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'both' },
    )
  }, [offset])

  const months =
    monthsToShow === 1
      ? [shiftMonthDate(baseMonth, offset)]
      : [shiftMonthDate(baseMonth, offset), shiftMonthDate(baseMonth, offset + 1)]
  const m1Key = toMonthKey(months[0]!)
  // When only one month is on screen we still key a second month off the base
  // so the query hooks stay unconditional (rules-of-hooks), but gate its fetch.
  const m2Key = toMonthKey(shiftMonthDate(baseMonth, offset + 1))

  // Fetch the two months actually on screen so navigating to past (or far
  // future) months shows real leaves — not just the today-anchored window.
  // Keyed identically to the dashboards' calendar queries, so the current +
  // next months share React Query's cache (no duplicate fetch).
  const cal1 = useQuery({
    queryKey: ['calendar', m1Key],
    queryFn: () => getCalendar({ month: m1Key }),
  })
  const cal2 = useQuery({
    queryKey: ['calendar', m2Key],
    queryFn: () => getCalendar({ month: m2Key }),
    enabled: monthsToShow === 2,
  })

  const leaves = useMemo(() => {
    const merged = new Map<string, CalendarLeave>()
    for (const l of [...(cal1.data?.leaves ?? []), ...(cal2.data?.leaves ?? [])]) {
      if (isHomeCalendarLeave(l, viewerId)) merged.set(l.id, l)
    }
    return [...merged.values()]
  }, [cal1.data, cal2.data, viewerId])

  const publicHolidays = useMemo(() => {
    const m = new Map<string, PublicHoliday>()
    for (const h of [
      ...(cal1.data?.publicHolidays ?? []),
      ...(cal2.data?.publicHolidays ?? []),
    ]) {
      m.set(h.date, h)
    }
    return [...m.values()]
  }, [cal1.data, cal2.data])

  const blackouts = useMemo(() => {
    const m = new Map<string, Blackout>()
    for (const b of [
      ...(cal1.data?.blackouts ?? []),
      ...(cal2.data?.blackouts ?? []),
    ]) {
      m.set(b.id, b)
    }
    return [...m.values()]
  }, [cal1.data, cal2.data])

  // Tap/click a day to reveal its leave details in a popover. On desktop the
  // day bars also show a hover tooltip, but touch has no hover — without this
  // the mobile leave-type dots (PR #114) would be undismissable dead pixels.
  // Mirrors the /calendar tap-to-popover, minus the coverage/leave-navigation
  // props that don't apply to an employee's own calendar.
  const [popoverDate, setPopoverDate] = useState<string | null>(null)
  const popoverAnchorRef = useRef<HTMLElement | null>(null)

  function handleDayClick(date: string, anchor: HTMLElement): void {
    const hasLeaves = leaves.some((l) => l.startDate <= date && l.endDate >= date)
    const hasHoliday = publicHolidays.some((h) => h.date === date)
    const hasBlackout = blackouts.some(
      (b) => b.startDate <= date && b.endDate >= date,
    )
    if (!hasLeaves && !hasHoliday && !hasBlackout) return
    popoverAnchorRef.current = anchor
    setPopoverDate(date)
  }

  // "days to year-end" from today
  const today = new Date(todayISO)
  const eoy = new Date(today.getFullYear(), 11, 31)
  const daysToEoy = Math.max(0, Math.ceil((eoy.getTime() - today.getTime()) / 86_400_000))

  return (
    <div className="th-shell" data-testid="two-month-home">
      {/* Header — month nav */}
      <div className="th-head">
        <div className="th-head-left">
          <button
            type="button"
            className="th-nav"
            aria-label="Previous months"
            onClick={() => setOffset((o) => o - 1)}
          >
            <Icon name="chevron_left" size={14} />
          </button>
          <button
            type="button"
            className="th-now"
            onClick={() => setOffset(0)}
            disabled={offset === 0}
            title={offset === 0 ? 'Showing current + next month' : 'Jump back to current + next month'}
          >
            Today
          </button>
          <button
            type="button"
            className="th-nav"
            aria-label="Next months"
            onClick={() => setOffset((o) => o + 1)}
          >
            <Icon name="chevron_right" size={14} />
          </button>
        </div>
      </div>

      {/* Two months — same grid + leave bars as the Team Calendar */}
      <div style={{ overflow: 'hidden' }}>
      <div
        className="th-months"
        ref={monthsRef}
        style={{
          willChange: 'transform',
          ...(monthsToShow === 1 ? { gridTemplateColumns: 'minmax(0,1fr)' } : {}),
        }}
      >
        {months.map((m) => {
          const monthKey = toMonthKey(m)
          return (
            <div className="th-month" key={monthKey}>
              <div className="th-month-h">
                <span>{monthLabel(m)}</span>
                <span className="th-month-y">{m.getFullYear()}</span>
              </div>
              <TeamCalendarGrid
                cells={buildMonthCells(monthKey)}
                leaves={leaves}
                blackouts={blackouts}
                publicHolidays={publicHolidays}
                today={todayISO}
                ownTeamId={null}
                cellMinHeight={68}
                maxBarRows={2}
                onCellClick={handleDayClick}
              />
            </div>
          )
        })}
      </div>
      </div>

      {/* Footer — stats + legend */}
      <div className="th-foot">
        <div className="th-stats">
          <span className="th-stat"><strong>{remainingDays}d</strong> bookable</span>
          <span className="th-stat"><strong>{daysToEoy}</strong> days to year-end</span>
          {nextLeaveDate ? (
            <span className="th-stat">
              Next leave{' '}
              <strong>
                {new Date(nextLeaveDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                })}
              </strong>
            </span>
          ) : null}
        </div>
        <div className="th-legend-row">
          <span className="th-leg">
            <span
              className="th-sw"
              style={{ background: 'var(--french-gray-2)', border: '1px solid var(--french-gray)' }}
            />
            Holiday
          </span>
          <span className="th-leg">
            <span className="th-sw th-sw-blackout" />
            Blackout
          </span>
        </div>
      </div>

      {popoverDate !== null ? (
        <CalendarDayPopover
          date={popoverDate}
          open={true}
          onClose={() => setPopoverDate(null)}
          anchorRef={popoverAnchorRef as RefObject<HTMLElement>}
          leaves={leaves.filter(
            (l) => l.startDate <= popoverDate && l.endDate >= popoverDate,
          )}
          {...(publicHolidays.find((h) => h.date === popoverDate) !== undefined
            ? {
                publicHoliday: {
                  name: publicHolidays.find((h) => h.date === popoverDate)!.name,
                },
              }
            : {})}
          blackouts={blackouts.filter(
            (b) => b.startDate <= popoverDate && b.endDate >= popoverDate,
          )}
        />
      ) : null}
    </div>
  )
}

// ─── EmployeeHomeDashboard ─────────────────────────────────────────────────────
// Top-level page. Matches prototype EmployeeDashboard (focus.kind === 'employee').
// Layout (prototype order):
//   1. One-line date + name header
//   2. dash-grid: HealthBarCard (left, 1fr) | BalancesCard (right, 2fr)
//      — when no health data, BalancesCard spans full width
//   3. TwoMonthHome calendar

export function EmployeeHomeDashboard({ firstName }: { firstName: string }): JSX.Element {
  const { user } = useSession()
  const realId = user?.id ?? null
  const effectiveId = useEffectiveEmployeeId(realId)
  const isImpersonating = effectiveId !== null && effectiveId !== realId
  const year = new Date().getFullYear()
  const today = new Date()
  const todayISO = toISO(today)

  // Health projection (shared with PersonalBlock's HealthBarCard via the same
  // query key, so this dedupes). Drives the forfeiture banner below the top
  // row — the "book annual before it lapses" nudge relocated out of the card.
  const health = useHealthBar(isImpersonating ? effectiveId : null).data
  const forfeitureRisk = health?.forfeitureRisk

  // ── Balances — also used below for "X days bookable" + next-leave stat ───
  // Keyed identically to PersonalBlock's query so React Query dedupes the
  // fetch; the personal block at the top of the page owns the rendering.
  const balancesQuery = useQuery({
    queryKey: ['me', 'balances', effectiveId, year],
    queryFn: () =>
      isImpersonating && effectiveId
        ? listEmployeeBalances(effectiveId, year)
        : listMyBalances(year),
    enabled: Boolean(effectiveId),
  })

  // ── Leaves — the viewer's full list, not a calendar window ────────────────
  // The Upcoming leave card must show every future booking this year, including
  // ones months out (e.g. a September leave viewed in July). The 2-month
  // calendar payload can't do that, so we read the full /me/leaves list here.
  // NOTE: this MUST NOT share MyLeavePage's ['me','leaves',…] key — that page
  // uses usePagedList, whose cached data is a per-page { rows, total } object
  // (keyed by page/pageSize), not a plain array. Sharing the key would let the
  // two clobber each other's cache shape (home expects an array → `.filter`
  // crash on the object). A distinct key keeps them independent.
  const leavesQuery = useQuery({
    queryKey: ['me', 'leaves', 'home-upcoming', effectiveId, isImpersonating ? 'as' : 'self'],
    queryFn: () =>
      isImpersonating && effectiveId
        ? listEmployeeLeaves(effectiveId)
        : listMyLeaves(),
    enabled: Boolean(effectiveId),
  })

  // ── Derived data ──────────────────────────────────────────────────────────
  // Viewer's own active (plannable) leaves that fall within the current year —
  // feeds the Upcoming leave list. Active-status + year filter here; the list
  // itself drops anything already ended and sorts by start date.
  const yearEndISO = `${year}-12-31`
  const ownLeaves = useMemo(
    () =>
      (Array.isArray(leavesQuery.data) ? leavesQuery.data : []).filter(
        (l) => HOME_CALENDAR_STATUSES.has(l.status) && l.startDate <= yearEndISO,
      ),
    [leavesQuery.data, yearEndISO],
  )

  // Date label for the top 1-liner — prototype style: "Mon, 26 May"
  const dateLabel = useMemo(
    () =>
      today.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [today.toDateString()],
  )

  return (
    <div className="page page-home" data-testid="employee-home">
      {/* ── Personal block: wellbeing + leave balances ───────────────────── */}
      <PersonalBlock />

      {/* ── Forfeiture banner: "book annual before it lapses" (was inside the
             wellbeing card; relocated here so the card stays about rhythm) ── */}
      {flags.forfeitNudge && health && forfeitureRisk && forfeitureRisk.severity !== 'none' ? (
        <ForfeitureBanner risk={forfeitureRisk} band={health.band} />
      ) : null}

      {/* ── Upcoming leave (primary): status-at-a-glance for the viewer ──── */}
      <div style={{ marginTop: 18 }}>
        <UpcomingLeaveList leaves={ownLeaves} todayISO={todayISO} />
      </div>

    </div>
  )
}

// ─── Style constants ──────────────────────────────────────────────────────────

const headerStyle: CSSProperties = {
  fontSize: 12.5,
  color: 'var(--text-3)',
  fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase',
  letterSpacing: '.08em',
  fontWeight: 600,
}
